import { useCallback, useEffect, useRef, useState } from 'react';

// 150ms delay allows smooth mouse movement from trigger to popover
const HOVER_CLOSE_DELAY_MS = 150;

interface UseHoverPopoverOptions {
  /**
   * Allow a click on the trigger to pin the popover open so its content can be read at
   * leisure, selected, and copied. While pinned, leaving the trigger does not close it —
   * only another click on the trigger, a click outside it, or an explicit `close()`, does.
   */
  isPinnable?: boolean;
}

export default function useHoverPopover({
  isPinnable = false,
}: UseHoverPopoverOptions = {}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isPinned, setIsPinned] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isOpen = Boolean(anchorEl);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
      closeTimeoutRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      clearCloseTimeout();
      setAnchorEl(event.currentTarget);
    },
    [clearCloseTimeout],
  );

  const handleMouseLeave = useCallback(() => {
    if (isPinned) {
      return;
    }

    scheduleClose();
  }, [isPinned, scheduleClose]);

  const close = useCallback(() => {
    setIsPinned(false);
    setAnchorEl(null);
  }, []);

  // Clicking the trigger toggles the pin. Anchoring here as well as on hover keeps the
  // popover reachable on touch devices, which never fire mouseenter.
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      clearCloseTimeout();

      if (isPinned) {
        close();

        return;
      }

      setAnchorEl(event.currentTarget);
      setIsPinned(true);
    },
    [clearCloseTimeout, close, isPinned],
  );

  return {
    anchorEl,
    isOpen,
    isPinned,
    triggerProps: {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...(isPinnable ? { onClick: handleClick } : {}),
    },
    popoverHandlers: {
      onMouseEnter: clearCloseTimeout,
      onMouseLeave: handleMouseLeave,
    },
    close,
  };
}
